import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";

export default function useAccountScope() {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedAccountId = searchParams.get("account_id") || "all";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAccounts(true);
      try {
        const { data } = await api.get("/accounts");
        if (!cancelled) {
          setAccounts(Array.isArray(data) ? data : []);
        }
      } catch (_) {
        if (!cancelled) {
          setAccounts([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingAccounts(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const validAccountIds = useMemo(() => new Set(accounts.map((a) => a.account_id)), [accounts]);

  useEffect(() => {
    if (selectedAccountId !== "all" && accounts.length > 0 && !validAccountIds.has(selectedAccountId)) {
      const next = new URLSearchParams(searchParams);
      next.delete("account_id");
      setSearchParams(next, { replace: true });
    }
  }, [accounts, selectedAccountId, validAccountIds, searchParams, setSearchParams]);

  const accountParam = selectedAccountId === "all" ? null : selectedAccountId;
  const selectedAccount = accountParam
    ? accounts.find((a) => a.account_id === accountParam) || null
    : null;

  const setSelectedAccountId = (nextAccountId) => {
    const next = new URLSearchParams(searchParams);
    if (!nextAccountId || nextAccountId === "all") {
      next.delete("account_id");
    } else {
      next.set("account_id", nextAccountId);
    }
    setSearchParams(next, { replace: true });
  };

  return {
    accounts,
    loadingAccounts,
    selectedAccountId,
    accountParam,
    selectedAccount,
    setSelectedAccountId,
  };
}
